/**
 * Static mock data for the customer-support inbox wireframe.
 *
 * Nothing here is wired to Supabase or Gmail — every thread is a scenario
 * lifted from the customer-service discovery call so the mockup shows how the
 * shared venue inbox, the assistant's suggestions and the approval queue would
 * feel for the staff who answer stockholm@ / copenhagen@ / oslo@ today.
 */

export type Mailbox = {
  id: string;
  address: string;
  city: string;
  synced: boolean;
};

export type Category =
  | "enquiry"
  | "change"
  | "cancellation"
  | "invoice"
  | "road-show"
  | "other";

export type Message = {
  id: string;
  from: { name: string; email: string };
  to: string;
  cc?: string;
  sentAt: string; // "Today · 11:24"
  /** Live messages: the exact time, shown in the viewer's own time zone. */
  sentIso?: string;
  body: string[]; // paragraphs
  html?: string; // sanitised HTML version of an email, shown when present
  /** Files attached to an email; url views it, url?download=1 saves it. */
  attachments?: Array<{ id: string; name: string; size: number; url: string }>;
  internal?: boolean; // internal note instead of an email
  draft?: boolean; // a Gmail draft, not sent yet
  author?: string; // note author
};

/**
 * A change the assistant suggests for a booking. Never applied by the AI:
 * staff apply or reject it.
 */
export type SuggestedAction = {
  id: string;
  /** The status transition rule that matched, e.g. "Customer confirms booking". */
  title: string;
  /** Why the AI thinks the rule applies. */
  reason: string;
  field: string;
  from: string;
  to: string;
  /** Live suggestions: the booking it changes. */
  booking?: LinkedBooking;
  /** The booking is no longer in the `from` status, so it cannot be applied. */
  stale?: boolean;
  /**
   * "change": what a change email asks for, listed in `changes`; staff make
   * it on the booking page and mark it done. Missing means a status change.
   */
  kind?: "status" | "change";
  changes?: SuggestedChange[];
  /** Staff already decided: the card stays, showing what they did. */
  decided?: "applied" | "rejected";
};

/** One requested booking change on a change card. */
export type SuggestedChange = {
  /** participants, date, time, add_on, show, language or other. */
  field: string;
  label: string; // "Participants", "Add-on: Pizza"
  from: string | null;
  to: string;
  /** The booking already has the asked value. */
  done: boolean;
};

export type SuggestedDraft = {
  /** The kept draft (ai_reply_drafts), to compare with the reply sent. */
  id?: string;
  sendAs: string;
  label: string;
  paragraphs: string[];
  sources: string[]; // knowledge base / db sources the draft leaned on
  /** What the AI wants staff to check or decide before sending. */
  note?: string;
  /** Why no usable draft was written. */
  error?: string;
};

export type AvailabilitySlot = {
  time: string;
  studio: string;
  capacity: string;
  price: string;
  status: "free" | "booked" | "recommended";
  note?: string;
};

export type LinkedBooking = {
  /** Database id + venue, set only when the booking exists in OnAir. */
  id?: number;
  venueId?: number;
  reference: string;
  show: string;
  studio: string;
  date: string;
  time: string;
  participants: number;
  minParticipants: number;
  language: string;
  status: string;
  total: string;
  paidStatus: string;
  invoiceRef?: string;
};

export type Thread = {
  id: string;
  mailboxId: string;
  customer: { name: string; email: string; company?: string; type: string };
  subject: string;
  preview: string;
  receivedAt: string;
  category: Category;
  /** Live threads: AI triage chose the category, not staff. */
  categoryByAi?: boolean;
  unread: boolean;
  starred: boolean;
  /** Live threads: listed in AI Settings → Drafts vs replies for review. */
  markedForReview?: boolean;
  done: boolean;
  needsFiling?: boolean;
  assignee?: string;
  mentions?: string[];
  msgCount: number;
  hasDraft: boolean;
  messages: Message[];
  /**
   * Live threads only. Messages load when a thread is opened, so the list
   * carries what it needs without them: which way the latest email went,
   * whether staff left notes, and a version that changes whenever the thread
   * does (so an opened thread knows to reload).
   */
  lastDirection?: "in" | "out";
  hasNotes?: boolean;
  version?: string;
  /** ISO time of the latest message; orders threads merged from pages. */
  sortKey?: string;
  /**
   * Where Gmail has the thread. "inbox" covers both Inbox and Sent (split
   * by lastDirection); the others hold drafts, spam and trash.
   */
  gmailFolder?: "inbox" | "drafts" | "junk" | "trash";
  /** Someone @mentioned the viewer in a note they have not cleared yet. */
  mentionedMe?: boolean;
  action?: SuggestedAction;
  draft?: SuggestedDraft;
  /** Live threads: the AI is writing a suggested reply right now. */
  draftWriting?: boolean;
  availability?: { heading: string; slots: AvailabilitySlot[] };
  booking?: LinkedBooking;
};

/** Someone a thread can be assigned to. `days` is a short second line. */
export type StaffMember = {
  id: string;
  name: string;
  initials: string;
  days: string;
};

export const STAFF: StaffMember[] = [
  { id: "thomas", name: "Thomas Ekström", initials: "TE", days: "Wed–Fri" },
  { id: "hampus", name: "Hampus Lind", initials: "HL", days: "Mon–Tue" },
  { id: "julius", name: "Julius Berg", initials: "JB", days: "On the road" },
  {
    id: "sebastian",
    name: "Sebastian Nyman",
    initials: "SN",
    days: "Site manager",
  },
  { id: "joakim", name: "Joakim Lublin", initials: "JL", days: "Owner" },
];

export const CURRENT_USER = STAFF[0];

export const MAILBOXES: Mailbox[] = [
  {
    id: "sthlm",
    address: "stockholm@onairgameshows.com",
    city: "Stockholm",
    synced: true,
  },
  {
    id: "cph",
    address: "copenhagen@onairgameshows.com",
    city: "Copenhagen",
    synced: true,
  },
  {
    id: "gbg",
    address: "gothenburg@onairgameshows.com",
    city: "Gothenburg",
    synced: false,
  },
  {
    id: "oslo",
    address: "oslo@onairgameshows.com",
    city: "Oslo",
    synced: false,
  },
];

export const CATEGORY_LABELS: Record<Category, string> = {
  enquiry: "Enquiry",
  change: "Change request",
  cancellation: "Cancellation",
  invoice: "Invoice",
  "road-show": "On the road",
  other: "Other",
};

export const THREADS: Thread[] = [
  {
    id: "t1",
    mailboxId: "sthlm",
    customer: {
      name: "Anna Lindqvist",
      email: "anna.lindqvist@nordicpayments.se",
      company: "Nordic Payments AB",
      type: "Corporate",
    },
    subject: "Team event – Thursday in May, ~18:00, 15 people",
    preview:
      "Hi! We're a team of 15 looking for a slot around 6 o'clock on a Thursday in May. What do you have and what would it cost?",
    receivedAt: "11:24",
    category: "enquiry",
    unread: true,
    starred: false,
    done: false,
    msgCount: 1,
    hasDraft: true,
    messages: [
      {
        id: "m1",
        from: {
          name: "Anna Lindqvist",
          email: "anna.lindqvist@nordicpayments.se",
        },
        to: "stockholm@onairgameshows.com",
        sentAt: "Today · 11:24",
        body: [
          "Hi!",
          "We're a team of 15 from Nordic Payments looking to do a game show together. Ideally a slot around 6 o'clock on a Thursday in May. What do you have available and what would it cost?",
          "We'd also like to stay for drinks afterwards if that's possible.",
          "Best regards,\nAnna",
        ],
      },
    ],
    draft: {
      sendAs: "Thomas Ekström",
      label: "Availability + recommendation",
      paragraphs: [
        "Hi Anna,",
        "Thanks for reaching out – a Thursday evening in May sounds great! I've checked the live schedule and here is what we have around 18:00:",
        "• Thu 7 May, 18:00 – Master Quiz Stockholm in Studio 1 (650 kr/person). Studio 1 has a minimum charge of 17 participants, so for 15 people you would pay for 17.",
        "• Thu 14 May, 15:00 – Nordic Challenge in Studio 2 (590 kr/person). Studio 2 fits 15 perfectly with no minimum surcharge, but it's an afternoon slot.",
        "• Thu 21 May, 18:30 – Mystery Game Challenge in Studio 3 (650 kr/person, min. 12).",
        "My recommendation for 15 people would be Studio 3 on 21 May – evening slot and no minimum-participant surcharge. The bar is open after every show, so drinks afterwards are no problem.",
        "You can see all slots and book directly here: onairgameshows.com/book/stockholm – or just reply and I'll hold one for you.",
        "Best,\nThomas",
      ],
      sources: [
        "Live availability · Stockholm · May 2026",
        "Price modifiers · Thursday evening",
        "KB: Studio minimum participants",
      ],
    },
    availability: {
      heading: "Thursdays in May · around 18:00 · 15 ppl",
      slots: [
        {
          time: "Thu 7 May · 18:00",
          studio: "Studio 1",
          capacity: "min 17 · max 45",
          price: "650 kr/pp",
          status: "free",
          note: "Min. charge 17 → 11 050 kr",
        },
        {
          time: "Thu 7 May · 18:00",
          studio: "Studio 2",
          capacity: "min 8 · max 20",
          price: "650 kr/pp",
          status: "booked",
        },
        {
          time: "Thu 14 May · 15:00",
          studio: "Studio 2",
          capacity: "min 8 · max 20",
          price: "590 kr/pp",
          status: "free",
          note: "Afternoon price",
        },
        {
          time: "Thu 14 May · 18:00",
          studio: "Studio 1",
          capacity: "min 17 · max 45",
          price: "650 kr/pp",
          status: "booked",
        },
        {
          time: "Thu 21 May · 18:30",
          studio: "Studio 3",
          capacity: "min 12 · max 24",
          price: "650 kr/pp",
          status: "recommended",
          note: "Best fit for 15 → 9 750 kr",
        },
        {
          time: "Thu 28 May · 18:00",
          studio: "Studio 1",
          capacity: "min 17 · max 45",
          price: "650 kr/pp",
          status: "free",
          note: "Min. charge 17",
        },
      ],
    },
  },
  {
    id: "t2",
    mailboxId: "sthlm",
    customer: {
      name: "Erik Johansson",
      email: "erik.johansson@scaniaparts.se",
      company: "Scania Parts",
      type: "Corporate",
    },
    subject: "Re: Booking BK-024817 – change number of participants",
    preview:
      "Hi, two colleagues can't make it on Friday. Could you change our booking from 18 to 16 people? Thanks!",
    receivedAt: "10:52",
    category: "change",
    unread: true,
    starred: true,
    done: false,
    assignee: "thomas",
    msgCount: 4,
    hasDraft: true,
    messages: [
      {
        id: "m1",
        from: {
          name: "Erik Johansson",
          email: "erik.johansson@scaniaparts.se",
        },
        to: "stockholm@onairgameshows.com",
        sentAt: "Today · 10:52",
        body: [
          "Hi,",
          "Two colleagues can't make it on Friday after all. Could you change our booking from 18 to 16 people? Same show, same time.",
          "Thanks!\nErik",
        ],
      },
      {
        id: "n1",
        internal: true,
        author: "Hampus Lind",
        from: { name: "Hampus Lind", email: "" },
        to: "",
        sentAt: "Today · 10:58",
        body: [
          "@Thomas this is the Scania group – they've booked 3 times this year, let's just do it for them and point them to the portal for next time.",
        ],
      },
    ],
    action: {
      id: "a1",
      title: "Update participant count on booking BK-024817",
      reason:
        "Explicit request in the email. 18 → 16 is an 11% reduction, stays above Studio 1 minimum (17 → billed at minimum charge).",
      field: "Participants",
      from: "18",
      to: "16",
    },
    draft: {
      sendAs: "Thomas Ekström",
      label: "Confirm change + portal nudge",
      paragraphs: [
        "Hi Erik,",
        "Done – your booking BK-024817 is now updated to 16 participants for Friday 25 September at 18:00, Master Quiz Stockholm in Studio 1.",
        "One thing to note: Studio 1 has a minimum charge of 17 participants, so the price stays at 17 × 650 kr = 11 050 kr. If you'd rather switch to Studio 2 (no minimum surcharge for 16) just let me know.",
        "Next time you can change participants yourself in a few seconds via your booking page: onairgameshows.com/my/BK-024817",
        "See you Friday!\nThomas",
      ],
      sources: [
        "Booking BK-024817 · Showtime",
        "KB: Studio minimum participants",
        "KB: Portal self-service nudge",
      ],
    },
    booking: {
      id: 24817,
      venueId: 2,
      reference: "BK-024817",
      show: "Master Quiz Stockholm",
      studio: "Studio 1",
      date: "Fri 25 Sep 2026",
      time: "18:00 – 19:30",
      participants: 18,
      minParticipants: 17,
      language: "Swedish",
      status: "Confirmed",
      total: "11 050 kr",
      paidStatus: "Invoice · unpaid",
      invoiceRef: "PO 4471-A",
    },
  },
  {
    id: "t3",
    mailboxId: "sthlm",
    customer: {
      name: "Maria Söderberg",
      email: "maria.soderberg@klarna.com",
      company: "Klarna",
      type: "Corporate",
    },
    subject: "Invoice 2026-1188 – wrong reference number",
    preview:
      "Our finance team bounced the invoice – the reference should be PO-88213 not PO-88231. Could you reissue?",
    receivedAt: "09:41",
    category: "invoice",
    unread: false,
    starred: false,
    done: false,
    msgCount: 2,
    hasDraft: true,
    messages: [
      {
        id: "m1",
        from: { name: "Maria Söderberg", email: "maria.soderberg@klarna.com" },
        to: "stockholm@onairgameshows.com",
        sentAt: "Today · 09:41",
        body: [
          "Hi,",
          "Our finance team bounced invoice 2026-1188 because the reference is wrong. It should be PO-88213, not PO-88231. Could you correct and resend it?",
          "Thanks,\nMaria",
        ],
      },
    ],
    action: {
      id: "a2",
      title: "Update invoice reference on 2026-1188 and resend",
      reason:
        "Reference-only change on an unpaid invoice. Amount and lines untouched.",
      field: "Invoice reference",
      from: "PO-88231",
      to: "PO-88213",
    },
    draft: {
      sendAs: "Thomas Ekström",
      label: "Invoice reissued",
      paragraphs: [
        "Hi Maria,",
        "Sorry about that – I've corrected the reference to PO-88213 and a new copy of invoice 2026-1188 is attached. Nothing else on the invoice has changed.",
        "Let me know if finance needs anything else.",
        "Best,\nThomas",
      ],
      sources: ["Invoice 2026-1188 · Fortnox", "KB: Invoice corrections"],
    },
    booking: {
      id: 24702,
      venueId: 2,
      reference: "BK-024702",
      show: "Nordic Challenge",
      studio: "Studio 2",
      date: "Thu 10 Sep 2026",
      time: "17:00 – 18:30",
      participants: 14,
      minParticipants: 8,
      language: "English",
      status: "Completed",
      total: "8 260 kr",
      paidStatus: "Invoice 2026-1188 · unpaid",
      invoiceRef: "PO-88231",
    },
  },
  {
    id: "t4",
    mailboxId: "sthlm",
    customer: {
      name: "Oskar Pettersson",
      email: "oskar@eventbolaget.se",
      company: "Eventbolaget",
      type: "Partner",
    },
    subject: "On-the-road show for conference in Uppsala – 120 people",
    preview:
      "We're hosting a conference dinner in Uppsala 12 Nov and would love a game show on site. 120 guests, two rounds?",
    receivedAt: "Yesterday",
    category: "road-show",
    unread: false,
    starred: false,
    done: false,
    assignee: "julius",
    mentions: ["julius"],
    msgCount: 6,
    hasDraft: false,
    messages: [
      {
        id: "m1",
        from: { name: "Oskar Pettersson", email: "oskar@eventbolaget.se" },
        to: "stockholm@onairgameshows.com",
        sentAt: "Yesterday · 16:10",
        body: [
          "Hi OnAir,",
          "We're hosting a conference dinner in Uppsala on 12 November and would love to have a game show on site between dinner and the party. Around 120 guests – would that be two rounds? What does the setup look like and what's the price?",
          "Oskar",
        ],
      },
      {
        id: "n1",
        internal: true,
        author: "Thomas Ekström",
        from: { name: "Thomas Ekström", email: "" },
        to: "",
        sentAt: "Yesterday · 16:25",
        body: [
          "@Julius this one's yours – on-the-road, 120 pax. Assigned to you.",
        ],
      },
    ],
  },
  {
    id: "t5",
    mailboxId: "sthlm",
    customer: {
      name: "Sofia Nilsson",
      email: "sofia.nilsson@gmail.com",
      type: "Individual",
    },
    subject: "Cancel booking BK-024890 (birthday, 22 Sep)",
    preview:
      "Unfortunately we have to cancel my sister's birthday booking on Monday. Can we get a refund?",
    receivedAt: "Yesterday",
    category: "cancellation",
    unread: false,
    starred: false,
    done: false,
    msgCount: 1,
    hasDraft: true,
    messages: [
      {
        id: "m1",
        from: { name: "Sofia Nilsson", email: "sofia.nilsson@gmail.com" },
        to: "stockholm@onairgameshows.com",
        sentAt: "Yesterday · 21:03",
        body: [
          "Hi,",
          "Unfortunately we have to cancel my sister's birthday booking on Monday 22 September (BK-024890). Something came up in the family. Is it possible to get a refund, or move it to a later date?",
          "Sofia",
        ],
      },
    ],
    action: {
      id: "a3",
      title: "Cancel booking BK-024890",
      reason:
        "Cancellation 4 days before the show – inside the 7-day window, so the cancellation policy says 50% fee. Needs a human decision.",
      field: "Booking status",
      from: "Confirmed",
      to: "Cancelled (50% fee)",
    },
    draft: {
      sendAs: "Thomas Ekström",
      label: "Offer reschedule before cancelling",
      paragraphs: [
        "Hi Sofia,",
        "Sorry to hear that – I hope everything is okay with the family.",
        "Because the show is on Monday we're inside the 7-day cancellation window, which normally means a 50% fee. Instead of cancelling, I'd suggest moving the booking: I can hold the same show for you on Sat 10 October at 16:00 or Sun 18 October at 15:00 at no extra cost.",
        "Just tell me which one works and I'll move it – or if you'd still prefer to cancel, let me know and I'll process it.",
        "Best,\nThomas",
      ],
      sources: [
        "KB: Cancellation policy (7 days)",
        "Live availability · October",
      ],
    },
    booking: {
      id: 24890,
      venueId: 2,
      reference: "BK-024890",
      show: "Mystery Game Challenge",
      studio: "Studio 3",
      date: "Mon 22 Sep 2026",
      time: "17:00 – 18:30",
      participants: 12,
      minParticipants: 12,
      language: "Swedish",
      status: "Confirmed",
      total: "7 800 kr",
      paidStatus: "Card · paid",
    },
  },
  {
    id: "t6",
    mailboxId: "sthlm",
    customer: {
      name: "Unknown sender",
      email: "reception@grandhotel.se",
      type: "Unknown",
    },
    subject: "Fwd: Christmas party 11 Dec – 45 guests",
    preview:
      "Forwarding from our events desk – client wants a game show for 45 on 11 Dec, which studio and can we get a group price?",
    receivedAt: "Yesterday",
    category: "enquiry",
    unread: true,
    starred: false,
    done: false,
    needsFiling: true,
    msgCount: 1,
    hasDraft: false,
    messages: [
      {
        id: "m1",
        from: {
          name: "Grand Hotel reception",
          email: "reception@grandhotel.se",
        },
        to: "stockholm@onairgameshows.com",
        sentAt: "Yesterday · 14:47",
        body: [
          "Forwarding from our events desk – a client is asking for a game show for 45 guests on Thursday 11 December, evening. Which studio would fit and is there a group price?",
        ],
      },
    ],
  },
  {
    id: "t7",
    mailboxId: "cph",
    customer: {
      name: "Mette Kristensen",
      email: "mk@novonordisk.com",
      company: "Novo Nordisk",
      type: "Corporate",
    },
    subject: "Hygge Quiz Night – can we do it in English?",
    preview:
      "Half our team doesn't speak Danish – is it possible to run the show in English on 3 Oct?",
    receivedAt: "Tue",
    category: "change",
    unread: false,
    starred: false,
    done: false,
    assignee: "hampus",
    msgCount: 3,
    hasDraft: true,
    messages: [
      {
        id: "m1",
        from: { name: "Mette Kristensen", email: "mk@novonordisk.com" },
        to: "copenhagen@onairgameshows.com",
        sentAt: "Tue · 13:12",
        body: [
          "Hi,",
          "Half our team doesn't speak Danish – is it possible to run the Hygge Quiz Night in English on 3 October instead?",
          "Mette",
        ],
      },
    ],
    action: {
      id: "a4",
      title: "Change show language on BK-003311",
      reason:
        "Language change affects host scheduling – the assigned host does not run shows in English.",
      field: "Language",
      from: "Danish",
      to: "English",
    },
    draft: {
      sendAs: "Hampus Lind",
      label: "Confirm English show",
      paragraphs: [
        "Hi Mette,",
        "Absolutely – we can run Hygge Quiz Night in English on 3 October. I've updated the booking and we'll make sure an English-speaking host is on that evening.",
        "Best,\nHampus",
      ],
      sources: ["Booking BK-003311 · Showtime", "KB: Show languages"],
    },
    booking: {
      id: 3311,
      venueId: 3,
      reference: "BK-003311",
      show: "Hygge Quiz Night",
      studio: "Studio A",
      date: "Fri 3 Oct 2026",
      time: "19:00 – 20:30",
      participants: 22,
      minParticipants: 10,
      language: "Danish",
      status: "Confirmed",
      total: "13 200 kr",
      paidStatus: "Invoice · paid",
    },
  },
  {
    id: "t8",
    mailboxId: "sthlm",
    customer: {
      name: "Lars Öhman",
      email: "lars.ohman@spotify.com",
      company: "Spotify",
      type: "Corporate",
    },
    subject: "Re: Thanks for a great evening!",
    preview:
      "Just wanted to say the whole team loved it. We'll be back in the spring.",
    receivedAt: "Mon",
    category: "other",
    unread: false,
    starred: false,
    done: true,
    msgCount: 2,
    hasDraft: false,
    messages: [
      {
        id: "m1",
        from: { name: "Lars Öhman", email: "lars.ohman@spotify.com" },
        to: "stockholm@onairgameshows.com",
        sentAt: "Mon · 09:30",
        body: [
          "Just wanted to say the whole team loved it. We'll be back in the spring!",
        ],
      },
    ],
  },
];

export const KB_SUGGESTIONS = [
  "Studio minimum participants",
  "Cancellation policy (7 days)",
  "Portal self-service nudge",
  "Invoice corrections",
  "Show languages",
];
